import { DotsSixVerticalIcon } from '@phosphor-icons/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Switch } from '@admin-panel/ui';
import type { LookGroupLookDTOType } from '../../../../schemas/styling';
import { getOptimizedSquareImage } from './helpers';

type SortableLookRowProps = {
  look: LookGroupLookDTOType;
  isBusy?: boolean;
  isUpdatingActive?: boolean;
  onActiveChange: (lookId: number, nextValue: boolean) => void;
};

const SortableLookRow = ({
  look,
  isBusy = false,
  isUpdatingActive = false,
  onActiveChange,
}: SortableLookRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: look.id,
    transition: {
      duration: 220,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
    zIndex: isDragging ? 10 : undefined,
  };

  const imageUrl = look.image || look.generated_image_url || look.source_image_url;
  const title = look.look_name || look.look_hook || `Look #${look.id}`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative ${isDragging ? 'opacity-80 shadow-sm' : ''}`}
    >
      <Button
        type="button"
        variant="ghost"
        size="small"
        aria-label={`Reorder look ${look.id}`}
        className="absolute top-1.5 left-1.5 z-10 min-w-0 cursor-grab bg-background/90 px-1.5 shadow-sm backdrop-blur active:cursor-grabbing"
        isDisabled={isBusy}
        {...attributes}
        {...listeners}
      >
        <DotsSixVerticalIcon size={16} />
      </Button>

      <div className="w-30 space-y-2">
        {imageUrl ? (
          <img
            src={getOptimizedSquareImage(imageUrl, 320)}
            alt={title}
            className="aspect-2/3 w-30 rounded-md border border-border/70 object-cover"
          />
        ) : (
          <div className="flex aspect-2/3 w-30 items-center justify-center rounded-md border border-border/70 bg-muted text-[10px] text-muted-foreground">
            No image
          </div>
        )}

        <div className="flex items-center justify-between gap-2 px-0.5">
          <span className="text-[11px] font-medium text-muted-foreground">Active</span>
          <Switch
            checked={look.is_active !== false}
            disabled={isBusy}
            isLoading={isUpdatingActive}
            size="small"
            onChange={checked => onActiveChange(look.id, checked)}
          />
        </div>
      </div>
    </div>
  );
};

export default SortableLookRow;
